import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  private openDatabase(
    dbName: string,
    storeName: string
  ): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const openRequest = indexedDB.open(dbName, 1);

      openRequest.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'id' });
        }
      };

      openRequest.onsuccess = (event: any) => {
        resolve(event.target.result);
      };

      openRequest.onerror = (error) => {
        reject(error);
      };
    });
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob); // Convert the Blob to Base64
    });
  }

  private async base64ToBlob(base64: string): Promise<Blob> {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays);
  }

  async saveData(
    key: string,
    data: any,
    dbName: string = 'appDB',
    storeName: string = 'objectsStore'
  ): Promise<void> {
    if (data?.img_blob) {
      data.img_blob = await this.blobToBase64(data.img_blob); // Convert Blob to Base64 before storing
    }

    const jsonString = JSON.stringify(data);

    try {
      const db = await this.openDatabase(dbName, storeName);
      const transaction = db.transaction(storeName, 'readwrite');
      const objectStore = transaction.objectStore(storeName);
      const request = objectStore.put({ id: key, data: jsonString });

      request.onerror = (error) => {
        console.error('Error saving object to IndexedDB:', error);
      };
    } catch (error) {
      console.error('Error opening IndexedDB database:', error);
    }
  }

  getData(
    key: string,
    dbName: string = 'appDB',
    storeName: string = 'objectsStore'
  ): Observable<any> {
    return new Observable((observer) => {
      this.openDatabase(dbName, storeName)
        .then((db) => {
          const transaction = db.transaction(storeName, 'readonly');
          const objectStore = transaction.objectStore(storeName);
          const request = objectStore.get(key);

          request.onsuccess = async(event: any) => {
            const jsonString = event.target.result?.data;
            if (jsonString) {
              const data = JSON.parse(jsonString);
              if (data?.img_blob) {
                data.img_blob = await this.base64ToBlob(data.img_blob); // Convert Base64 to Blob
              }
              observer.next(data);
              observer.complete(); // Mark as complete once the data is sent
            } else {
              observer.error('No object found in IndexedDB');
            }
          };

          request.onerror = (error) => {
            observer.error(error);
          };
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  getAllKeys(
    dbName: string = 'appDB',
    storeName: string = 'objectsStore'
  ): Observable<any[]> {
    return new Observable((observer) => {
      // Use this.openDatabase to open the database
      this.openDatabase(dbName, storeName)
        .then((db) => {
          const transaction = db.transaction(storeName, 'readonly');
          const objectStore = transaction.objectStore(storeName);

          const keys: any[] = [];

          // Use getAllKeys() method if supported
          if (objectStore.getAllKeys) {
            const request = objectStore.getAllKeys();

            request.onsuccess = () => {
              observer.next(request.result);
              observer.complete();
            };

            request.onerror = (error) => {
              observer.error(error);
            };
          } else {
            // If getAllKeys is not supported, fall back to using cursors
            const cursorRequest = objectStore.openCursor();

            cursorRequest.onsuccess = (event: any) => {
              const cursor = event.target.result;
              if (cursor) {
                keys.push(cursor.key);
                cursor.continue(); // Continue iterating to the next record
              } else {
                observer.next(keys); // Once the cursor finishes, return the collected keys
                observer.complete();
              }
            };

            cursorRequest.onerror = (error) => {
              observer.error(error);
            };
          }
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }

  deleteDatabase(dbName: string = 'appDB'): Observable<void> {
    return new Observable((observer) => {
      const deleteRequest = indexedDB.deleteDatabase(dbName);

      deleteRequest.onsuccess = () => {
        observer.next();
        observer.complete();
      };

      deleteRequest.onerror = (error) => {
        observer.error(error);
      };

      deleteRequest.onblocked = () => {
        observer.error('Database deletion blocked');
      };
    });
  }

  async deleteData(
    key: string,
    dbName: string = 'appDB',
    storeName: string = 'objectsStore'
  ): Promise<void> {
    const db = await this.openDatabase(dbName, storeName);
    const transaction = db.transaction(storeName, 'readwrite');
    const objectStore = transaction.objectStore(storeName);
    const request = objectStore.delete(key);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
